import React from 'react';
import { Typography } from '@navi/web-ui/lib/primitives';
import Button from '@primitives/Button';
import { Popper, PopperTrigger, PopperContent } from '@cp/src/components/Popper/Popper';
import dayjs from 'dayjs';
import { CalendarIcon } from '@cp/src/assets/icons/CalendarIcon';
import { AddressMetadata } from '../interfaces';
import { DateFormat } from '@cp/src/utils/DateHelper';
import CopyToClipboardIcon from '@cp/src/assets/icons/CopyToClipboardIcon';

interface RecentActivityBadgeProps {
  metadata: AddressMetadata;
}

const RecentActivityBadge: React.FC<RecentActivityBadgeProps> = ({ metadata }) => {
  const {
    recentActivityDate,
    recentActivityUpdatedOn,
    recentActivityAddedByName,
    recentActivityAddedByEmail
  } = metadata || {};

  if (!recentActivityDate) {
    return null;
  }

  const formattedDate = dayjs(recentActivityDate).format(DateFormat.LONG_DATE_FORMAT_WITHOUT_TIME);
  const formattedUpdatedOn = recentActivityUpdatedOn
    ? dayjs(recentActivityUpdatedOn).format(DateFormat.LONG_DATE_FORMAT_WITHOUT_TIME)
    : '';

  const handleCopyEmail = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(recentActivityAddedByEmail || '');
  };

  return (
    <Popper placement="bottom" hideStrategy="referenceHidden">
      <PopperTrigger>
        <button
          type="button"
          className="flex items-center gap-1 px-2 py-1 bg-[var(--pale-yellow)] border border-solid border-[var(--yellow-Chiffon,#fef5d0)] rounded cursor-pointer hover:bg-[var(--yellow-Chiffon,#fef5d0)]"
        >
          <CalendarIcon />
          <Typography variant="p4" className="text-[var(--pending-fill-color,#c8a311)] font-normal">
            Recent activity:
          </Typography>
          <Typography variant="p4" className="text-[var(--pending-fill-color,#c8a311)] font-medium">
            {formattedDate}
          </Typography>
        </button>
      </PopperTrigger>
      <PopperContent
        headerText="Recent activity by"
        className="popperWrapper"
        arrowColor="var(--orange-oldlase)"
        style={{ zIndex: 1301 }}
      >
        <div className="flex flex-col px-3 py-2 w-[280px] gap-2">
          <div className="flex flex-row justify-between">
            <Typography variant="p3" className="text-sm" color="var(--grayscale-2)">
              Name
            </Typography>
            <Typography variant="p3" className="text-sm font-medium" color="var(--grayscale-2)">
              {recentActivityAddedByName || 'NA'}
            </Typography>
          </div>

          <div className="flex flex-row justify-between">
            <Typography variant="p3" className="text-sm" color="var(--grayscale-2)">
              Email
            </Typography>
            <div className="flex flex-row gap-[2px] items-center">
              <Typography variant="p3" className="text-sm font-medium" color="var(--grayscale-2)">
                {recentActivityAddedByEmail || 'NA'}
              </Typography>
              {recentActivityAddedByEmail && (
                <Button variant="text" onClick={handleCopyEmail} className="!p-0">
                  <CopyToClipboardIcon width={16} height={16} />
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-row justify-between">
            <Typography variant="p3" className="text-sm" color="var(--grayscale-2)">
              Updated on
            </Typography>
            <Typography variant="p3" className="text-sm font-medium" color="var(--grayscale-2)">
              {formattedUpdatedOn || 'NA'}
            </Typography>
          </div>
        </div>
      </PopperContent>
    </Popper>
  );
};

export default RecentActivityBadge;
